"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { useVenueCache } from "@/contexts/venue-context";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Plus, Pencil, Trash2, MapPin, MoreVertical } from "lucide-react";
import LoadingSpinner from "@/components/loading-spinner";
import { ROLES, type RoleId } from "@/lib/constants/roles";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";

interface Studio {
  id: number;
  name: string;
  // "on_site" = fixed physical studio; "on_road" = studio used for road shows.
  // Persisted to studios.type. Kits are a separate entity (see /kits).
  type?: "on_site" | "on_road";
  capacity: number | null;
  status: string;
  created_at?: string;
  venue_id?: number;
  status_id?: number;
  statuses?: {
    id: number;
    name: string;
  };
  studio_medias?: Array<{
    id: number;
    url: string;
    type: string;
    is_primary: boolean;
  }>;
}

interface StudioStatus {
  id: number;
  name: string;
}

interface StudiosPageClientProps {
  venueId: string;
  venueName: string;
  initialStudios: Studio[];
  studioStatuses: StudioStatus[];
  userRoleId?: RoleId;
}

export default function StudiosPageClient({
  venueId,
  venueName,
  initialStudios,
  studioStatuses,
  userRoleId = ROLES.BOOKING_STAFF,
}: StudiosPageClientProps) {
  const router = useRouter();
  const { refresh: refreshVenueCache } = useVenueCache();

  // Determine the portal path based on user role
  const portalPath =
    userRoleId === ROLES.SUPER_ADMIN ? "/superadmin" : "/booking";
  const [studios, setStudios] = useState<Studio[]>(initialStudios);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [editingStudio, setEditingStudio] = useState<Studio | null>(null);
  const [deletingStudio, setDeletingStudio] = useState<Studio | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    type: "on_site" as "on_site" | "on_road",
    capacity: "",
    status_id:
      studioStatuses.find((s) => s.name === "active")?.id.toString() || "",
  });

  const resetForm = () => {
    setFormData({
      name: "",
      type: "on_site",
      capacity: "",
      status_id:
        studioStatuses.find((s) => s.name === "active")?.id.toString() || "",
    });
    setEditingStudio(null);
  };

  // Refresh studios data from server
  const fetchStudios = async () => {
    try {
      const response = await fetch(`/api/venues/${venueId}/studios`);
      if (response.ok) {
        const data = await response.json();
        setStudios(data.studios || []);
      }
    } catch (error) {
      console.error("Failed to refresh studios:", error);
    }
  };

  // Capacity is required for on-site studios but optional for on-road studios.
  const capacityRequired = formData.type === "on_site";

  const handleAddStudio = async () => {
    if (!formData.name || (capacityRequired && !formData.capacity)) return;

    setIsLoading(true);
    try {
      const response = await fetch(`/api/venues/${venueId}/studios`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: formData.name,
          type: formData.type,
          capacity: formData.capacity ? parseInt(formData.capacity) : null,
          status:
            studioStatuses.find((s) => s.id === parseInt(formData.status_id))
              ?.name || "active",
          infos: [
            {
              lang: "en",
              information: "",
            },
          ],
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to create studio");
      }

      const result = await response.json();
      console.log("Create response:", result);

      // Close dialog immediately after successful API response
      setIsAddDialogOpen(false);
      resetForm();

      // Show success message
      toast.success(result.message || "Studio created successfully");

      // Refresh data in background (don't await)
      Promise.all([
        fetchStudios(), // Update local studios list
        refreshVenueCache(), // Update venue context (dashboard, show forms)
      ]).catch(console.error);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to create studio",
      );
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditStudio = (studio: Studio) => {
    setEditingStudio(studio);
    // Find the status_id from the status name
    const statusObj = studioStatuses.find((s) => s.name === studio.status);
    setFormData({
      name: studio.name,
      type: studio.type === "on_road" ? "on_road" : "on_site",
      capacity: studio.capacity?.toString() || "",
      status_id:
        statusObj?.id.toString() ||
        studioStatuses.find((s) => s.name === "active")?.id.toString() ||
        "",
    });
  };

  const handleUpdateStudio = async () => {
    if (
      !editingStudio ||
      !formData.name ||
      (capacityRequired && !formData.capacity)
    )
      return;

    setIsLoading(true);
    try {
      const response = await fetch(
        `/api/venues/${venueId}/studios/${editingStudio.id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: formData.name,
            type: formData.type,
            capacity: formData.capacity ? parseInt(formData.capacity) : null,
            status:
              studioStatuses.find((s) => s.id === parseInt(formData.status_id))
                ?.name || "active",
          }),
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to update studio");
      }

      const result = await response.json();
      console.log("Update response:", result);

      // Close dialog immediately after successful API response
      setIsLoading(false);
      setEditingStudio(null);
      resetForm();

      toast.success(result.message || "Studio updated successfully");

      // Refresh data in background (don't await)
      Promise.all([
        fetchStudios(), // Update local studios list
        refreshVenueCache(), // Update venue context (dashboard, show forms)
      ]).catch(console.error);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to update studio",
      );
      console.error(error);

      // Reset loading state on error
      setIsLoading(false);
    }
  };

  const confirmDeleteStudio = async () => {
    if (!deletingStudio) return;

    setIsLoading(true);
    try {
      console.log("Deleting studio:", deletingStudio.id);
      const response = await fetch(
        `/api/venues/${venueId}/studios/${deletingStudio.id}`,
        {
          method: "DELETE",
        },
      );

      const result = await response.json();
      console.log("Delete response:", { status: response.status, result });

      if (!response.ok) {
        console.log("Response not ok, throwing error");
        throw new Error(result.error || "Failed to delete studio");
      }

      console.log("Response OK, proceeding to close dialog");

      // Close dialog immediately after successful API response
      setIsLoading(false);
      setDeletingStudio(null);
      console.log("Dialog closed immediately after API success");

      // Update local state and show success message
      setStudios(studios.filter((s) => s.id !== deletingStudio.id));
      toast.success("Studio deleted successfully");

      // Refresh venue context cache in background (don't await)
      refreshVenueCache().catch(console.error);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to delete studio";
      toast.error(errorMessage);
      console.error("Delete studio error:", error);

      // Reset states on error too
      setTimeout(() => {
        setIsLoading(false);
        setDeletingStudio(null);
        console.log(
          "States reset after error: isLoading=false, deletingStudio=null",
        );
      }, 0);
    }
  };

  const getStatusBadgeStyle = (status: string) => {
    switch (status) {
      case "active":
        return "bg-green-100 text-green-800 border-green-200";
      case "maintenance":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "inactive":
        return "bg-gray-200 text-gray-700 border-gray-300";
      default:
        // Default to active style for unknown/empty status
        return "bg-green-100 text-green-800 border-green-200";
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold">Studios</h1>
            <p className="text-gray-600 mt-2">
              Manage studios for {venueName}
            </p>
          </div>

          <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => resetForm()}>
                <Plus className="h-4 w-4 mr-2" />
                Add Studio
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader className="pb-4">
                <DialogTitle className="text-xl font-semibold">
                  Add New Studio
                </DialogTitle>
                <DialogDescription className="text-gray-600">
                  Create a new studio for {venueName}. Fill in the details
                  below.
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-6 py-4">
                <div className="space-y-2">
                  <Label
                    htmlFor="name"
                    className="text-sm font-medium text-gray-700"
                  >
                    Name *
                  </Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    placeholder="e.g., Studio Alpha, VR Room 1"
                    className="w-full"
                  />
                </div>

                <div className="space-y-2">
                  <Label
                    htmlFor="type"
                    className="text-sm font-medium text-gray-700"
                  >
                    Type *
                  </Label>
                  <Select
                    value={formData.type}
                    onValueChange={(value) =>
                      setFormData({
                        ...formData,
                        type: value as "on_site" | "on_road",
                      })
                    }
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="on_site">On-site</SelectItem>
                      <SelectItem value="on_road">On the road</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500">
                    "On the road" studios are used for road shows. Equipment
                    kits are managed separately under Kits.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label
                    htmlFor="capacity"
                    className="text-sm font-medium text-gray-700"
                  >
                    Maximum capacity{capacityRequired ? " *" : " (optional)"}
                  </Label>
                  <Input
                    id="capacity"
                    type="number"
                    min="1"
                    max="100"
                    value={formData.capacity}
                    onChange={(e) =>
                      setFormData({ ...formData, capacity: e.target.value })
                    }
                    placeholder="e.g., 20"
                    className="w-full"
                  />
                  <p className="text-xs text-gray-500">
                    Maximum number of people this studio can accommodate
                  </p>
                </div>

                <div className="space-y-2">
                  <Label
                    htmlFor="status"
                    className="text-sm font-medium text-gray-700"
                  >
                    Status *
                  </Label>
                  <Select
                    value={formData.status_id}
                    onValueChange={(value) =>
                      setFormData({ ...formData, status_id: value })
                    }
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      {studioStatuses.map((status) => (
                        <SelectItem
                          key={status.id}
                          value={status.id.toString()}
                        >
                          {status.name.charAt(0).toUpperCase() +
                            status.name.slice(1)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <DialogFooter className="pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => setIsAddDialogOpen(false)}
                  className="mr-2"
                  disabled={isLoading}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleAddStudio}
                  disabled={
                    !formData.name ||
                    (capacityRequired && !formData.capacity) ||
                    isLoading
                  }
                >
                  {isLoading ? (
                    <LoadingSpinner size="sm" className="mr-2" />
                  ) : (
                    <Plus className="h-4 w-4 mr-2" />
                  )}
                  {isLoading ? "Adding..." : "Add Studio"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Studios List */}
        {studios.length > 0 && (
          <div className="bg-white rounded-lg border shadow overflow-hidden">
            <Table>
              <TableHeader className="bg-gray-50">
                <TableRow className="border-b-2">
                  <TableHead className="text-gray-700 font-semibold w-2/6 px-6 py-4">
                    Name
                  </TableHead>
                  <TableHead className="text-gray-700 font-semibold w-1/6 px-6 py-4">
                    Type
                  </TableHead>
                  <TableHead className="text-gray-700 font-semibold w-1/6 px-6 py-4">
                    Capacity
                  </TableHead>
                  <TableHead className="text-gray-700 font-semibold w-1/6 px-6 py-4">
                    Status
                  </TableHead>
                  <TableHead className="text-right text-gray-700 font-semibold w-1/6 px-6 py-4">
                    Actions
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {studios.map((studio) => (
                  <TableRow key={studio.id} className="hover:bg-gray-50">
                    <TableCell className="font-medium text-gray-900 px-6 py-4">
                      {studio.name}
                    </TableCell>
                    <TableCell className="px-6 py-4">
                      <Badge
                        variant="secondary"
                        className={`text-xs ${
                          studio.type === "on_road"
                            ? "bg-blue-100 text-blue-800 border-blue-200"
                            : "bg-gray-100 text-gray-700 border-gray-200"
                        }`}
                      >
                        {studio.type === "on_road" ? "On the road" : "On-site"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-gray-600 px-6 py-4">
                      {studio.capacity ? `${studio.capacity} people` : "-"}
                    </TableCell>
                    <TableCell className="px-6 py-4">
                      <Badge
                        variant="secondary"
                        className={`text-xs capitalize ${getStatusBadgeStyle(studio.status || "active")}`}
                      >
                        {studio.status || "active"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right px-6 py-4">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => handleEditStudio(studio)}
                          >
                            <Pencil className="h-4 w-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => setDeletingStudio(studio)}
                            className="text-red-600 focus:text-red-600"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Empty State */}
        {studios.length === 0 && (
          <div className="bg-white rounded-lg border shadow overflow-hidden py-12">
            <div className="text-center">
              <div className="mx-auto w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                <MapPin className="h-8 w-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                No studios yet
              </h3>
              <p className="text-gray-600 mb-6 max-w-md mx-auto">
                Get started by adding your first studio to {venueName}. Studios
                are where your game shows take place.
              </p>
              <Button
                onClick={() => setIsAddDialogOpen(true)}
                className="bg-purple-600 hover:bg-purple-700"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add your first studio
              </Button>
            </div>
          </div>
        )}

        {/* Edit Studio Dialog */}
        <Dialog
          open={!!editingStudio}
          onOpenChange={(open) => {
            if (!open) {
              setEditingStudio(null);
              resetForm();
            }
          }}
        >
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader className="pb-4">
              <DialogTitle className="text-xl font-semibold">
                Edit Studio
              </DialogTitle>
              <DialogDescription className="text-gray-600">
                Update information for "{editingStudio?.name}".
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6 py-4">
              <div className="space-y-2">
                <Label
                  htmlFor="edit-name"
                  className="text-sm font-medium text-gray-700"
                >
                  Name *
                </Label>
                <Input
                  id="edit-name"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  placeholder="e.g., Studio Alpha, VR Room 1"
                  className="w-full"
                />
              </div>

              <div className="space-y-2">
                <Label
                  htmlFor="edit-type"
                  className="text-sm font-medium text-gray-700"
                >
                  Type *
                </Label>
                <Select
                  value={formData.type}
                  onValueChange={(value) =>
                    setFormData({
                      ...formData,
                      type: value as "on_site" | "on_road",
                    })
                  }
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="on_site">On-site</SelectItem>
                    <SelectItem value="on_road">On the road</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500">
                  "On the road" studios are used for road shows. Equipment kits
                  are managed separately under Kits.
                </p>
              </div>

              <div className="space-y-2">
                <Label
                  htmlFor="edit-capacity"
                  className="text-sm font-medium text-gray-700"
                >
                  Maximum capacity{capacityRequired ? " *" : " (optional)"}
                </Label>
                <Input
                  id="edit-capacity"
                  type="number"
                  min="1"
                  max="100"
                  value={formData.capacity}
                  onChange={(e) =>
                    setFormData({ ...formData, capacity: e.target.value })
                  }
                  placeholder="e.g., 20"
                  className="w-full"
                />
                <p className="text-xs text-gray-500">
                  Maximum number of people this studio can accommodate
                </p>
              </div>

              <div className="space-y-2">
                <Label
                  htmlFor="edit-status"
                  className="text-sm font-medium text-gray-700"
                >
                  Status *
                </Label>
                <Select
                  value={formData.status_id}
                  onValueChange={(value) =>
                    setFormData({ ...formData, status_id: value })
                  }
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    {studioStatuses.map((status) => (
                      <SelectItem key={status.id} value={status.id.toString()}>
                        {status.name.charAt(0).toUpperCase() +
                          status.name.slice(1)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <DialogFooter className="pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => {
                  setEditingStudio(null);
                  resetForm();
                }}
                className="mr-2"
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                onClick={handleUpdateStudio}
                disabled={
                  !formData.name ||
                  (capacityRequired && !formData.capacity) ||
                  isLoading
                }
                className="bg-purple-600 hover:bg-purple-700"
              >
                {isLoading ? (
                  <LoadingSpinner size="sm" className="mr-2" />
                ) : (
                  <Pencil className="h-4 w-4 mr-2" />
                )}
                {isLoading ? "Updating..." : "Update Studio"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={!!deletingStudio}
          onOpenChange={(open) => !open && setDeletingStudio(null)}
        >
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader className="pb-4">
              <DialogTitle className="text-xl font-semibold text-red-600">
                Delete studio
              </DialogTitle>
              <DialogDescription className="text-gray-600">
                Are you sure you want to delete "{deletingStudio?.name}"? This
                action cannot be undone.
              </DialogDescription>
            </DialogHeader>

            <div className="py-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0">
                    <Trash2 className="h-5 w-5 text-red-400" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-red-800">
                      This will permanently delete:
                    </h3>
                    <ul className="mt-2 text-sm text-red-700 space-y-1">
                      <li>• Studio "{deletingStudio?.name}"</li>
                      <li>• All associated shows and bookings</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter className="pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => setDeletingStudio(null)}
                className="mr-2"
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                onClick={confirmDeleteStudio}
                className="bg-red-600 hover:bg-red-700 text-white"
                disabled={isLoading}
              >
                {isLoading ? (
                  <LoadingSpinner size="sm" className="mr-2" />
                ) : (
                  <Trash2 className="h-4 w-4 mr-2" />
                )}
                {isLoading ? "Deleting..." : "Delete studio"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
